
import React, { createContext, useContext, useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";

type User = {
  id: string;
  email: string;
  role: "admin" | "host" | "guest";
  full_name?: string;
};

type UserContextType = {
  user: User | null;
  isLoading: boolean;
  error: Error | null;
  setUser: (user: User | null) => void;
  logout: () => Promise<void>;
};

const UserContext = createContext<UserContextType | undefined>(undefined);

export const UserProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const { data: { session }, error } = await supabase.auth.getSession();
        
        if (error) {
          throw error;
        }

        if (session?.user) {
          // Fetch user profile with role information
          const { data: profileData, error: profileError } = await supabase
            .from("profiles")
            .select("id, role, full_name")
            .eq("id", session.user.id)
            .single();

          if (profileError) {
            throw profileError;
          }

          // Create a User object manually with email from auth session
          // and other fields from profile
          setUser({
            id: session.user.id,
            email: session.user.email || '',
            role: profileData.role as "admin" | "host" | "guest",
            full_name: profileData.full_name
          });
        }
      } catch (err) {
        console.error("Error fetching user:", err);
        setError(err instanceof Error ? err : new Error("Failed to fetch user"));
      } finally {
        setIsLoading(false);
      }
    };

    fetchUser();

    // Set up authentication listener
    const { data: authListener } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (event === "SIGNED_IN" && session) {
          try {
            // Fetch user profile with role information
            const { data: profileData, error: profileError } = await supabase
              .from("profiles")
              .select("id, role, full_name")
              .eq("id", session.user.id)
              .single();

            if (profileError) throw profileError;

            // Create a User object manually with email from auth session
            // and other fields from profile
            setUser({
              id: session.user.id,
              email: session.user.email || '',
              role: profileData.role as "admin" | "host" | "guest",
              full_name: profileData.full_name
            });
          } catch (error) {
            console.error("Error fetching profile:", error);
            setUser(null);
          }
        } else if (event === "SIGNED_OUT") {
          setUser(null);
        }
      }
    );

    return () => {
      if (authListener && authListener.subscription) {
        authListener.subscription.unsubscribe();
      }
    };
  }, []);

  const logout = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      setUser(null);
    } catch (err) {
      console.error("Error during logout:", err);
      throw err;
    }
  };

  return (
    <UserContext.Provider value={{ user, isLoading, error, setUser, logout }}>
      {children}
    </UserContext.Provider>
  );
};

export const useUser = () => {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error("useUser must be used within a UserProvider");
  }
  return context;
};
